<template>
  <div class="mt-5 mb-5">
    <b-message v-if="!canEndLabellisationInstruction" type="is-info">
      {{ $t("graePage.dossiersLabellisationList.explanations-no-end-possible") }}
    </b-message>
    <b-message v-else type="is-info">
      {{ $t("graePage.dossiersLabellisationList.explanations-end-possible") }}
    </b-message>

    <div v-if="canEndLabellisationInstruction && hasRightsToEndLabellisationInstruction()">
      <b-button class="button is-primary" @click="endLabellisation">
        {{ $t("graePage.dossiersLabellisationList.end-instruction") }}
      </b-button>
      <br /><br />
    </div>

    <TableFilterView
      :columns="columns"
      :initial-filters="activeFilters"
      @update-active-filters="updateActiveFilters"
      @update-visible-columns="updateVisibleColumns"
      @export-csv="exportLabellisationClotureCsv"
      @export-xls="exportLabellisationClotureXls"
    />
    <b-table
      :ref="tableRef"
      :striped="config.isStriped"
      :hoverable="config.isHoverable"
      :debounce-search="config.searchDebounce"
      :aria-next-label="config.nextLabel()"
      :aria-previous-label="config.previousLabel()"
      :aria-page-label="config.pageLabel()"
      :aria-current-label="config.currentPageLabel()"
      :per-page="config.perPage"
      :sticky-header="config.stickyHeader"
      :current-page="currentPagedSortedParameter.pageNumber"
      :pagination-position="config.paginationPosition"
      paginated
      backend-pagination
      :sort-icon="config.sortIcon"
      :sort-icon-size="config.sortIconSize"
      backend-sorting
      sort-multiple
      :sort-multiple-data="buefySortingPriority"
      :total="total"
      :data="projects"
      :loading="isLoading"
      @sort="onSort"
      @sorting-priority-removed="sortingPriorityRemoved"
      @page-change="onPageChange"
    >
      <b-table-column
        v-for="column in columns"
        :key="column.field"
        v-bind="column"
        :visible="column.visible"
      >
        <template #default="props">
          <div>
            <!-- default fields -->
            <div
              v-if="column.field === 'codeDepartement'"
              class="clickable"
              @click="showProjectDetailsPage($event, props.row['projetId'], props.row['id'])"
            >
              <div v-if="props.row['etablissements'].length">
                {{ props.row["etablissements"][0].codeDepartement.replace(/^0+/, "") }}
              </div>
            </div>
            <div
              v-if="column.field === 'ville'"
              class="clickable"
              @click="showProjectDetailsPage($event, props.row['projetId'], props.row['id'])"
            >
              <div v-if="props.row['etablissements'].length">
                {{ props.row["etablissements"][0].ville }}
              </div>
            </div>
            <div
              v-if="column.field === 'etablissement.name'"
              class="clickable"
              @click="showProjectDetailsPage($event, props.row['projetId'], props.row['id'])"
            >
              <div v-if="props.row['etablissements']">
                <div
                  v-for="organisme in props.row['etablissements'].filter(
                    (o: any) => o && o.id && o.nom,
                  )"
                  :key="organisme.id"
                >
                  {{ organisme.nom }}
                </div>
              </div>
            </div>
            <div v-if="column.field === 'etablissement.codeEtablissement'">
              <div v-if="props.row['etablissements']">
                <div
                  v-for="organisme in props.row['etablissements'].filter(
                    (o: any) => o && o.id && o.nom,
                  )"
                  :key="props.row['id'] + '_' + organisme.codeEtablissement"
                >
                  {{ organisme.codeEtablissement }}
                </div>
              </div>
            </div>
            <div
              v-if="column.field === 'projet.nom'"
              class="clickable"
              @click="showProjectDetailsPage($event, props.row['projetId'], props.row['id'])"
            >
              {{ props.row["nom"] }}
            </div>
            <div
              v-if="column.field === 'typeDossier'"
              class="clickable"
              @click="showProjectDetailsPage($event, props.row['projetId'], props.row['id'])"
            >
              {{ $t(props.row.typeDossier) }}
            </div>
            <div
              v-if="column.field === 'statut'"
              class="clickable"
              @click="showProjectDetailsPage($event, props.row['projetId'], props.row['id'])"
            >
              <StatutDossierTag
                v-if="loggedUser"
                :dossier="props.row"
                can-see-instruction-comment
                :logged-user="loggedUser"
              />
            </div>
            <div v-if="column.field === 'natureAireEducative'">
              {{ props.row["natureAireEducative"] }}
            </div>
            <div
              v-if="column.field === 'enseignant.name'"
              class="clickable"
              @click="showProjectDetailsPage($event, props.row['projetId'], props.row['id'])"
            >
              <div
                v-for="(utilisateur, i) in props.row['enseignants'].filter(
                  (u: any) => u && u.id && u.nom,
                )"
                :key="i + '_' + utilisateur.id"
              >
                {{ utilisateur.prenom + " " + utilisateur.nom }}
              </div>
            </div>
            <div
              v-if="column.field === 'structure.name'"
              class="clickable"
              @click="showProjectDetailsPage($event, props.row['projetId'], props.row['id'])"
            >
              <div v-if="props.row['structures']">
                <div
                  v-for="organisme in props.row['structures'].filter(
                    (o: any) => o && o.id && o.nom,
                  )"
                  :key="organisme.id"
                >
                  {{ organisme.nom }}
                </div>
              </div>
            </div>
            <div
              v-if="column.field === 'referent.name'"
              class="clickable"
              @click="showProjectDetailsPage($event, props.row['projetId'], props.row['id'])"
            >
              <div
                v-for="(utilisateur, i) in props.row['referents'].filter(
                  (u: any) => u && u.id && u.nom,
                )"
                :key="i + '_' + utilisateur.id"
              >
                {{ utilisateur.prenom + " " + utilisateur.nom }}
              </div>
            </div>
            <div
              v-if="column.field === 'dateModification'"
              class="clickable"
              @click="showProjectDetailsPage($event, props.row['projetId'], props.row['id'])"
            >
              {{ formatDate(props.row[column.field]) }}
            </div>
            <div
              v-if="column.field === 'instructeurs.name'"
              class="clickable"
              @click="showProjectDetailsPage($event, props.row['projetId'], props.row['id'])"
            >
              <div v-if="props.row['evaluations']">
                <div
                  v-for="evaluation in props.row['evaluations']"
                  :key="evaluation.instructeur.id"
                >
                  {{ evaluation.instructeur.prenom + " " + evaluation.instructeur.nom }}
                </div>
              </div>
            </div>
            <div
              v-if="column.field === 'deroule'"
              class="clickable"
              @click="showProjectDetailsPage($event, props.row['projetId'], props.row['id'])"
            >
              {{ props.row["evaluationFinale"].derouleNote }}
            </div>
            <div
              v-if="column.field === 'diffusion'"
              class="clickable"
              @click="showProjectDetailsPage($event, props.row['projetId'], props.row['id'])"
            >
              {{ props.row["evaluationFinale"].adhesionNote }}
            </div>
            <div
              v-if="column.field === 'enseignements'"
              class="clickable"
              @click="showProjectDetailsPage($event, props.row['projetId'], props.row['id'])"
            >
              {{ props.row["evaluationFinale"].integrationNote }}
            </div>
            <div
              v-if="column.field === 'implication'"
              class="clickable"
              @click="showProjectDetailsPage($event, props.row['projetId'], props.row['id'])"
            >
              {{ props.row["evaluationFinale"].implicationNote }}
            </div>
            <div v-if="column.field === 'commentaires'" class="clickable">
              <LabellisationEvaluationCommentsPopup
                :project-annuel="props.row"
                :evaluation="props.row.evaluationFinale"
                :is-final="true"
                :read-only="true"
                @evaluation-updated="handleEvaluationUpdated"
                @comment-changed="handleCommentChanged"
              />
            </div>
            <div
              v-if="column.field === 'evaluation.mention'"
              class="clickable"
              @click="showProjectDetailsPage($event, props.row['projetId'], props.row['id'])"
            >
              {{ $t("instruction-panel.instruction." + props.row["evaluationFinale"].mention) }}
            </div>
            <div v-if="column.field === 'action'">
              <div class="buttons">
                <b-button
                  icon-left="eye"
                  type="is-link is-outlined"
                  :title="$t('consult-projet')"
                  :label="$t('consult')"
                  @click="consult($event, props.row['projetId'], props.row['id'])"
                >
                </b-button>
                <b-button
                  icon-left="comment-alt"
                  type="is-link is-outlined"
                  :title="$t('instructionPage.labellisations.cloture.renvoyer')"
                  :label="$t('instructionPage.labellisations.cloture.renvoyer')"
                  :disabled="!hasRightToModify"
                  @click="renvoyer($event, props.row['id'])"
                >
                </b-button>
              </div>
            </div>

            <!-- hidden fields -->
            <div v-if="column.field === 'thematique'" class="clamp">
              <span v-for="(thematique, index) in props.row[column.field]" :key="thematique">
                {{ $t("instruction." + thematique)
                }}<span v-if="index != props.row[column.field].length - 1">{{
                  $t("virgule")
                }}</span>
              </span>
            </div>
            <div v-if="column.field === 'objectsConcretRealisation'" class="clamp">
              <span v-for="(objet, index) in props.row[column.field]" :key="objet">
                {{ $t("instruction." + objet)
                }}<span v-if="index != props.row[column.field].length - 1">{{
                  $t("virgule")
                }}</span>
              </span>
            </div>
            <div v-if="column.field === 'milieuGenerique' && props.row[column.field]" class="clamp">
              <span>{{ $t("instruction." + props.row[column.field]) }}</span>
            </div>
            <div v-if="column.field === 'milieuInteretParticulier'" class="clamp">
              <span v-for="(milieu, index) in props.row[column.field]" :key="milieu">
                {{ $t("instruction." + milieu)
                }}<span v-if="index != props.row[column.field].length - 1">{{
                  $t("virgule")
                }}</span>
              </span>
            </div>

            <!-- non customised fields -->
            <div
              v-if="!column.custom"
              class="clickable"
              @click="showProjectDetailsPage($event, props.row['projetId'], props.row['id'])"
            >
              <div>{{ props.row[column.field] }}</div>
            </div>
          </div>
        </template>
      </b-table-column>
    </b-table>
  </div>
</template>

<script lang="ts">
import { EnumHelper } from "@/model/bean/EnumHelper"
import {
  FilterDTO,
  PaginationOrder,
  ProjetAnnuelDTO,
  UtilisateurDTO,
} from "@/model/bean/GeneratedDTOs"
import { Filters } from "@/model/bean/Filters"
import RouterUtils from "@/router/RouterUtils"
import { InfoReportingService } from "@/services/log/InfoReportingService"
import { InstructionService } from "@/services/rest/instruction/InstructionService"
import { LoginService } from "@/services/rest/login/LoginService"
import { UsersService } from "@/services/rest/users/UsersService"
import { defineComponent } from "vue"
import Column from "@/views/tables/Column"
import TableFilterView from "@/views/tables/TableFilterView.vue"
import StatutDossierTag from "@/views/dossier/StatutDossierTag.vue"
import LabellisationEvaluationCommentsPopup from "./LabellisationEvaluationCommentsPopup.vue"
import { useTableView } from "@/hooks/useTableView"
import { SortOrder } from "@/model/SortOrder"
import { BButton, BMessage, BTable, BTableColumn } from "buefy"

import DateUtils from "@/services/DateUtils"

export default defineComponent({
  name: "LabellisationClotureView",
  components: {
    TableFilterView,
    LabellisationEvaluationCommentsPopup,
    StatutDossierTag,
    BButton,
    BMessage,
    BTable,
    BTableColumn,
  },
  props: {
    graeId: {
      type: String,
      required: true,
    },
    activeSubTab: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      loginService: LoginService.INSTANCE,
      instructionService: InstructionService.INSTANCE,
      infoReportingService: InfoReportingService.INSTANCE,
      usersService: UsersService.INSTANCE,
      loggedUser: null as UtilisateurDTO | null,
      projects: [] as Array<ProjetAnnuelDTO>,
      canEndLabellisationInstruction: false,
      hasRightToModify: false,
      // Utilisation du hook useTableView
      tableView: null as any,
    }
  },
  computed: {
    // Propriétés réactives du tableau
    tableRef(): string {
      return this.tableView.state.tableRef
    },

    isLoading(): boolean {
      return this.tableView.state.isLoading
    },

    columns(): Array<Column> {
      return this.tableView.state.columns
    },

    currentPagedSortedParameter(): any {
      return this.tableView.state.currentPagedSortedParameter
    },

    buefySortingPriority(): Array<any> {
      return this.tableView.state.buefySortingPriority
    },

    total(): number {
      return this.tableView.state.total
    },

    config(): any {
      return this.tableView.state.config
    },

    activeFilters(): Array<any> {
      return this.tableView.state.activeFilters
    },
  },

  created() {
    this.loggedUser = this.loginService.getLoggedUser() || null
    if (!this.loggedUser || !this.loggedUser.id) {
      return
    }
    this.checkEndLabellisationInstruction()
    this.hasRightToModify =
      this.loggedUser.profil !== "OBSERVATEUR_NATIONAL" &&
      this.loggedUser.profil !== "ACCOMPAGNATEUR_REGIONAL"

    // Initialisation du hook useTableView
    this.tableView = useTableView({
      router: this.$router,
      route: this.$route,
      i18n: this.$i18n,
      tableRef: "labellisation-cloture-table",
      getInitialColumns: () => this.getInitialColumns(),
      getDefaultSort: () => this.getDefaultSort(),
      getInitialActiveFilters: () => this.getInitialActiveFilters(),
      reloadData: async () => {
        await this.reloadData()
      },
      reloadDataWhenMounted: true,
    })
  },

  mounted() {
    this.tableView.initMounted()
  },

  methods: {
    // Méthodes mappées depuis tableView
    updateVisibleColumns(columns: Array<Column>): void {
      this.tableView.updateVisibleColumns(columns)
    },

    onPageChange(newPage: string | number): void {
      const pageNum = typeof newPage === "string" ? parseInt(newPage) : newPage
      this.tableView.onPageChange(pageNum)
    },

    onSort(field: string | undefined, order: SortOrder): void {
      if (field) {
        this.tableView.onSort(field, order)
      }
    },

    sortingPriorityRemoved(field: string | undefined): void {
      if (field) {
        this.tableView.sortingPriorityRemoved(field)
      }
    },

    updateActiveFilters(filters: Array<FilterDTO>): void {
      this.tableView.updateActiveFilters(filters)
    },

    updateTotal(total: number): void {
      this.tableView.updateTotal(total)
    },

    getInitialActiveFilters(): Array<FilterDTO> {
      return new Array<FilterDTO>()
    },

    exportLabellisationClotureCsv(): void {
      const statutFilter = Filters.asFilter({
        property: "statut",
        values: ["INSTRUCTION_TERMINEE"],
      })
      const onlyValidatedFilter = Filters.asFilter({
        property: "evaluationFinaleValidated",
        values: ["true"],
      })
      this.instructionService.exportLabellisationsClosCsv(this.graeId, [
        ...this.activeFilters,
        statutFilter,
        onlyValidatedFilter,
      ])
    },

    exportLabellisationClotureXls(): void {
      const statutFilter = Filters.asFilter({
        property: "statut",
        values: ["INSTRUCTION_TERMINEE"],
      })
      const onlyValidatedFilter = Filters.asFilter({
        property: "evaluationFinaleValidated",
        values: ["true"],
      })
      this.instructionService.exportLabellisationsClosXls(this.graeId, [
        ...this.activeFilters,
        statutFilter,
        onlyValidatedFilter,
      ])
    },

    formatDate(date: Date): string {
      return DateUtils.formatDate(date)
    },

    async checkEndLabellisationInstruction(): Promise<void> {
      this.canEndLabellisationInstruction =
        await this.instructionService.canEndLabellisationForGrae(this.graeId)
    },

    hasRightsToEndLabellisationInstruction(): boolean {
      if (this.loggedUser) {
        return this.usersService.canModifyGrae(this.loggedUser, this.graeId)
      }
      return false
    },

    endLabellisation(): void {
      this.infoReportingService.dialogWithCancel(
        this.$t("graePage.dossiersLabellisationList.confirm-popup-title").toString(),
        this.$t("graePage.dossiersLabellisationList.confirm-popup-message").toString(),
        this.$t("graePage.dossiersLabellisationList.confirm-popup-ok-button").toString(),
        async () => {
          this.tableView.state.isLoading = true
          try {
            await this.instructionService.endLabellisationForGrae(this.graeId)
            this.tableView.state.isLoading = false
            this.infoReportingService.success(
              this.$t("graePage.dossiersLabellisationList.ended-labellisation").toString(),
            )
            this.canEndLabellisationInstruction = false
            await this.tableView.reload()
          } catch (e: any) {
            this.tableView.state.isLoading = false
            this.infoReportingService.error(
              this.$t("graePage.dossiersLabellisationList.end-labellisation-failure").toString(),
              e as Error,
            )
          }
        },
        () => {
          // Nothing to do
        },
        "is-danger",
      )
    },

    async reloadData(): Promise<void> {
      this.tableView.state.isLoading = true
      const statutFilter = Filters.asFilter({
        property: "statut",
        values: ["INSTRUCTION_TERMINEE"],
      })
      const onlyValidatedFilter = Filters.asFilter({
        property: "evaluationFinaleValidated",
        values: ["true"],
      })
      try {
        const paginatedProjectsList = await this.instructionService.getLabellisations(
          this.graeId,
          this.currentPagedSortedParameter,
          [...this.activeFilters, statutFilter, onlyValidatedFilter],
          undefined,
        )
        this.projects = paginatedProjectsList.elements
        this.updateTotal(paginatedProjectsList.count)
      } catch (e: any) {
        // Silent catch
      }
      this.tableView.state.isLoading = false
    },

    getInitialColumns(): Array<Column> {
      return [
        // default fields
        {
          field: "codeDepartement",
          label: this.$t("graePage.details.projets.dpt").toString(),
          sortable: false,
          backEndSearchable: true,
          visible: true,
          custom: true,
          canBeAdded: true,
        },
        {
          field: "ville",
          label: this.$t("graePage.details.projets.ville").toString(),
          sortable: false,
          backEndSearchable: true,
          visible: true,
          custom: true,
          canBeAdded: true,
        },
        {
          field: "etablissement.name",
          label: this.$t("graePage.details.projets.etablissements").toString(),
          sortable: false,
          backEndSearchable: true,
          visible: true,
          custom: true,
          canBeAdded: true,
        },
        {
          field: "etablissement.codeEtablissement",
          label: this.$t("graePage.details.projets.uais").toString(),
          sortable: false,
          backEndSearchable: true,
          visible: true,
          custom: true,
          canBeAdded: true,
        },
        {
          field: "projet.nom",
          label: this.$t("graePage.details.projets.nom").toString(),
          sortable: true,
          backEndSearchable: true,
          visible: true,
          custom: true,
          canBeAdded: true,
        },
        {
          field: "natureAireEducative",
          label: this.$t("graePage.details.projets.nature-ae").toString(),
          sortable: true,
          backEndSearchable: true,
          possibleSearchValues: EnumHelper.allNatures,
          visible: true,
          custom: true,
          canBeAdded: true,
        },
        {
          field: "typeDossier",
          label: this.$t("graePage.details.projets.typeDossier").toString(),
          sortable: true,
          backEndSearchable: true,
          possibleSearchValues: EnumHelper.allTypeDossiers,
          visible: true,
          custom: true,
          canBeAdded: true,
        },
        {
          field: "statut",
          label: this.$t("graePage.details.projets.statut").toString(),
          sortable: false,
          backEndSearchable: false,
          visible: false,
          custom: true,
          canBeAdded: true,
        },
        {
          field: "enseignant.name",
          label: this.$t("graePage.details.projets.enseignants").toString(),
          sortable: false,
          backEndSearchable: false,
          visible: false,
          custom: true,
          canBeAdded: true,
        },
        {
          field: "referent.name",
          label: this.$t("graePage.details.projets.referents").toString(),
          sortable: false,
          backEndSearchable: false,
          visible: false,
          custom: true,
          canBeAdded: true,
        },
        {
          field: "structure.name",
          label: this.$t("graePage.details.projets.structures").toString(),
          sortable: false,
          backEndSearchable: false,
          visible: false,
          custom: true,
          canBeAdded: true,
        },
        {
          field: "dateModification",
          label: this.$t("graePage.details.projets.dateModification").toString(),
          sortable: false,
          backEndSearchable: false,
          visible: false,
          custom: true,
          canBeAdded: true,
        },
        {
          field: "instructeurs.name",
          label: this.$t("graePage.details.projets.instructeurs").toString(),
          sortable: false,
          backEndSearchable: true,
          visible: true,
          custom: true,
          canBeAdded: true,
        },
        {
          field: "deroule",
          label: this.$t("instructionPage.labellisations.harmonisation.note.deroule").toString(),
          sortable: false,
          backEndSearchable: false,
          visible: true,
          custom: true,
          canBeAdded: true,
        },
        {
          field: "diffusion",
          label: this.$t("instructionPage.labellisations.harmonisation.note.diffusion").toString(),
          sortable: false,
          backEndSearchable: false,
          visible: true,
          custom: false,
          canBeAdded: true,
        },
        {
          field: "enseignements",
          label: this.$t(
            "instructionPage.labellisations.harmonisation.note.enseignements",
          ).toString(),
          sortable: false,
          backEndSearchable: false,
          visible: true,
          custom: false,
          canBeAdded: true,
        },
        {
          field: "implication",
          label: this.$t(
            "instructionPage.labellisations.harmonisation.note.implication",
          ).toString(),
          sortable: false,
          backEndSearchable: false,
          visible: true,
          custom: false,
          canBeAdded: true,
        },
        {
          field: "commentaires",
          label: this.$t(
            "instructionPage.labellisations.harmonisation.note.commentaires",
          ).toString(),
          sortable: false,
          backEndSearchable: false,
          visible: true,
          custom: false,
          canBeAdded: true,
        },
        {
          field: "evaluation.mention",
          label: this.$t("instructionPage.labellisations.harmonisation.note.mention").toString(),
          sortable: true,
          backEndSearchable: false,
          visible: true,
          custom: false,
          canBeAdded: true,
        },
        {
          field: "action",
          label: this.$t("projetsListPage.actions").toString(),
          sortable: false,
          backEndSearchable: false,
          visible: true,
          custom: true,
          canBeAdded: true,
        },
        // hidden fields
        {
          field: "thematique",
          label: this.$t("graePage.details.projets.thematiques").toString(),
          sortable: false,
          backEndSearchable: false,
          visible: false,
          custom: true,
          canBeAdded: true,
        },
        {
          field: "objectsConcretRealisation",
          label: this.$t("graePage.details.projets.objets-concrets-de-realisation").toString(),
          sortable: false,
          backEndSearchable: false,
          visible: false,
          custom: true,
          canBeAdded: true,
        },
        {
          field: "milieuGenerique",
          label: this.$t("graePage.details.projets.milieu-generique").toString(),
          sortable: false,
          backEndSearchable: false,
          visible: false,
          custom: true,
          canBeAdded: true,
        },
        {
          field: "milieuInteretParticulier",
          label: this.$t("graePage.details.projets.milieux-interets-particuliers").toString(),
          sortable: false,
          backEndSearchable: false,
          visible: false,
          custom: true,
          canBeAdded: true,
        },
      ]
    },

    getDefaultSort(): Array<PaginationOrder> {
      const defaultSort = new PaginationOrder()
      defaultSort.clause = "projet.nom"
      defaultSort.desc = false
      return [defaultSort]
    },

    showProjectDetailsPage(event: Event, projetId: string, projetAnnuelId: string): void {
      // Do not foward click event to row (would trigger modal)
      event.stopPropagation()
      this.$router.push(RouterUtils.getRouteForProjectDetails(projetId, projetAnnuelId))
    },

    consult(event: Event, projetId: string, projetAnnuelId: string): void {
      // Do not foward click event to row (would trigger modal)
      event.stopPropagation()
      const route = this.$router.resolve(
        RouterUtils.getRouteForProjectDetails(projetId, projetAnnuelId),
      )
      window.open(route.href)
    },

    async renvoyer(event: Event, projetAnnuelId: string): Promise<void> {
      // Do not foward click event to row (would trigger modal)
      event.stopPropagation()
      try {
        await this.instructionService.desapprouveEvaluationFinale(projetAnnuelId)

        this.infoReportingService.success(
          this.$t("instructionPage.labellisations.cloture.renvoyer-en-harmonisation").toString(),
        )
        this.projects = []
        await this.tableView.reload()
      } catch (e: any) {
        this.infoReportingService.error(
          this.$t(
            "instructionPage.labellisations.cloture.renvoyer-en-harmonisation-error",
          ).toString(),
          e as Error,
        )
      }
    },

    handleEvaluationUpdated(): void {
      // Recharger les données après mise à jour de l'évaluation
      this.tableView.reload()
    },

    handleCommentChanged(): void {
      // Recharger les données après modification du commentaire
      this.tableView.reload()
    },
  },
})
</script>

<style lang="scss">
.clamp {
  text-align: left;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  overflow: hidden;
}
</style>
